import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useSocketEvent } from '../context/SocketContext';
import { StatusBadge } from '../components/Badges';
import { ChevronLeft, ChevronRight, Clock, User, CalendarDays } from 'lucide-react';
import WhatsAppButton from '../components/WhatsAppButton';
import { todayISO } from '../utils/clinicDate';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function padDate(y, m, d) {
  return `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

export default function CalendarPage() {
  const { showToast } = useToast();
  const today = todayISO();
  const todayDate = new Date();

  const [viewYear,  setViewYear]  = useState(todayDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(todayDate.getMonth()); // 0-indexed
  const [selected,  setSelected]  = useState(today);
  const [appointments, setAppointments] = useState([]); // all appts in current month
  const [dayAppts, setDayAppts] = useState([]);           // for selected date
  const [loading, setLoading] = useState(false);
  const [todayLabOrders, setTodayLabOrders]           = useState([]);
  const [lowStockAlerts, setLowStockAlerts]           = useState([]);

  // ── Load today's lab + pharmacy reminders ─────────────────────────────────
  const loadTodayReminders = useCallback(async () => {
    try {
      // Lab orders expected today or pending
      const [labRes, stockRes] = await Promise.all([
        api.get('/lab', { params: { expectedDate: today, status: 'ordered' } }),
        api.get('/pharmacy/items', { params: { lowStock: 'true' } }),
      ]);
      setTodayLabOrders(labRes.data.orders || []);
      setLowStockAlerts(stockRes.data.items?.filter(i => (i.stockQuantity || 0) <= (i.lowStockThreshold ?? 3)) || []);
    } catch { /* non-fatal */ }
  }, [today]);

  useEffect(() => { loadTodayReminders(); }, [loadTodayReminders]);

  // ── Load month appointments ────────────────────────────────────────────────
  const loadMonth = useCallback(async (year, month) => {
    setLoading(true);
    try {
      const from = padDate(year, month, 1);
      // Last day of month
      const lastDay = new Date(year, month + 1, 0).getDate();
      const to = padDate(year, month, lastDay);
      const res = await api.get('/patients/calendar', { params: { from, to } });
      setAppointments(res.data.appointments || []);
    } catch { showToast('Could not load calendar.', 'error'); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { loadMonth(viewYear, viewMonth); }, [viewYear, viewMonth, loadMonth]);

  // Filter appts for the selected day
  useEffect(() => {
    setDayAppts(appointments.filter((a) => a.appointmentDate === selected).sort((a,b) => (a.appointmentTime||'') > (b.appointmentTime||'') ? 1 : -1));
  }, [appointments, selected]);

  useSocketEvent('queue:updated', () => loadMonth(viewYear, viewMonth));

  // ── Navigation ─────────────────────────────────────────────────────────────
  function prevMonth() {
    if (viewMonth === 0) { setViewYear((y) => y-1); setViewMonth(11); }
    else setViewMonth((m) => m-1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear((y) => y+1); setViewMonth(0); }
    else setViewMonth((m) => m+1);
  }
  function goToday() {
    setViewYear(todayDate.getFullYear());
    setViewMonth(todayDate.getMonth());
    setSelected(today);
  }

  // ── Build calendar grid ────────────────────────────────────────────────────
  const firstDay = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
  const [labDueDates, setLabDueDates] = useState({});

  useEffect(() => {
    api.get('/lab', { params:{ type:'buy', status:'ordered', limit:200 } })
      .then(r => {
        const map = {};
        (r.data.orders||[]).forEach(o => {
          if (o.expectedDate) map[o.expectedDate] = (map[o.expectedDate]||0)+1;
        });
        setLabDueDates(map);
      }).catch(()=>{});
  }, []);

  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();

  // Map date string → appointment count for dots
  const countByDate = {};
  appointments.forEach((a) => {
    if (a.appointmentDate) countByDate[a.appointmentDate] = (countByDate[a.appointmentDate]||0)+1;
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout title="Calendar" subtitle="Appointment schedule at a glance">

      {/* ── Today's reminders & low-stock alerts ──────────────────────────── */}
      {(todayLabOrders.length > 0 || lowStockAlerts.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 16 }}>
          {/* Lab orders due today */}
          {todayLabOrders.length > 0 && (
            <div className="tilt3d-scene">
            <div className="card tilt3d-card" style={{ padding: 14, borderLeft: '4px solid var(--color-warning)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-warning)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                🧪 Lab Orders Expected Today ({todayLabOrders.length})
              </div>
              {todayLabOrders.map(o => (
                <div key={o._id} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>{o.itemName}</span>
                  <span style={{ color: 'var(--color-ink-faint)' }}>{o.labName || 'Lab'}</span>
                </div>
              ))}
            </div>
            </div>
          )}

          {/* Low stock alerts */}
          {lowStockAlerts.length > 0 && (
            <div className="tilt3d-scene">
            <div className="card tilt3d-card" style={{ padding: 14, borderLeft: '4px solid var(--color-danger)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-danger)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                ⚠️ Low Stock Alerts ({lowStockAlerts.length})
              </div>
              {lowStockAlerts.map(item => (
                <div key={item._id} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600 }}>{item.name}</span>
                  <span style={{ color: 'var(--color-danger)', fontWeight: 700 }}>
                    {item.stockQuantity} {item.unit} left
                    <span style={{ color: 'var(--color-ink-faint)', fontWeight: 400 }}> (threshold: {item.lowStockThreshold})</span>
                  </span>
                </div>
              ))}
            </div>
            </div>
          )}
        </div>
      )}

      <div className="calendar-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 20, alignItems: 'start' }}>

        {/* Calendar grid */}
        <div className="card" style={{ padding: 20 }}>
          {/* Month navigation */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <button className="btn btn-icon btn-outline" onClick={prevMonth} title="Previous month"><ChevronLeft size={18} /></button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 18, fontWeight: 800 }}>{MONTHS[viewMonth]} {viewYear}</span>
              {(viewYear !== todayDate.getFullYear() || viewMonth !== todayDate.getMonth()) && (
                <button className="btn btn-outline btn-sm" onClick={goToday}>Today</button>
              )}
            </div>
            <button className="btn btn-icon btn-outline" onClick={nextMonth} title="Next month"><ChevronRight size={18} /></button>
          </div>

          {/* Day name headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
            {DAYS.map((d) => (
              <div key={d} style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--color-ink-faint)', padding: '4px 0' }}>{d}</div>
            ))}
          </div>

          {/* Calendar cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {cells.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} />;
              const dateStr = padDate(viewYear, viewMonth, d);
              const isToday   = dateStr === today;
              const isSelected = dateStr === selected;
              const count = countByDate[dateStr] || 0;

              return (
                <button key={dateStr} onClick={() => setSelected(dateStr)}
                  className="calendar-day-cell"
                  style={{
                    aspect: '1 / 1', borderRadius: 10, border: 'none', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 3, position: 'relative', padding: 4,
                    background: isSelected
                      ? 'linear-gradient(155deg, color-mix(in srgb, var(--color-primary) 88%, white), var(--color-primary))'
                      : isToday ? 'var(--color-primary-soft)' : 'transparent',
                    color: isSelected ? 'white' : isToday ? 'var(--color-primary)' : 'var(--color-ink)',
                    fontWeight: isToday || isSelected ? 700 : 400,
                    fontSize: 14,
                    transition: 'background 0.12s, box-shadow 0.12s, transform 0.15s',
                    // The selected day is genuinely raised off the grid, not
                    // just a different flat color — a bright top-inset edge
                    // plus a deep outer shadow reads as a glossy, raised
                    // badge. Today (unselected) gets a subtle inset ring so
                    // it still reads as distinct.
                    boxShadow: isSelected
                      ? 'inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.15), 0 4px 8px rgba(0,0,0,0.18), 0 10px 20px -6px rgba(0,0,0,0.22)'
                      : isToday
                        ? 'inset 0 0 0 1.5px var(--color-primary)'
                        : 'none',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-primary-soft)'; else e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = isToday ? 'var(--color-primary-soft)' : 'transparent'; else e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {d}
                  {count > 0 && (
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      background: isSelected ? 'rgba(255,255,255,0.35)' : 'var(--color-accent)',
                      color: 'white',
                      borderRadius: 100, padding: '1px 5px', minWidth: 18, textAlign: 'center',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 1px 2px rgba(0,0,0,0.25)',
                    }}>{count}</span>
                  )}
                  {labDueDates[dateStr] > 0 && (
                    <span title={`${labDueDates[dateStr]} lab order(s) due`} style={{
                      fontSize: 9, fontWeight: 700,
                      background: '#dc2626', color: 'white',
                      borderRadius: 100, padding: '1px 4px', minWidth: 14, textAlign: 'center',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 1px 2px rgba(0,0,0,0.25)',
                    }}>L{labDueDates[dateStr]}</span>
                  )}
                </button>
              );
            })}
          </div>

          {loading && <div style={{ textAlign: 'center', color: 'var(--color-ink-faint)', fontSize: 13, marginTop: 12 }}>Loading…</div>}
        </div>

        {/* Day detail panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card lift3d-card" style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <CalendarDays size={18} color="var(--color-primary)" />
              <span style={{ fontWeight: 700, fontSize: 15 }}>{formatDate(selected)}</span>
              {selected === today && <span className="badge badge-in-progress" style={{ fontSize: 11 }}>Today</span>}
            </div>

            {dayAppts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--color-ink-faint)', fontSize: 13 }}>
                No appointments scheduled for this day.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dayAppts.map((a, i) => (
                  <div key={a._id} className="lift3d-card stagger-in" style={{
                    padding: '10px 14px', borderRadius: 10,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 4px 8px -4px rgba(0,0,0,0.1)',
                    '--stagger-i': i,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <User size={13} />{a.name}
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-ink-faint)', fontWeight: 400 }}>{a.patientUID}</span>
                        </div>
                        {a.appointmentSlot && (
                          <div style={{ fontSize: 12, color: 'var(--color-primary)', fontWeight: 600, marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={11} />{a.appointmentSlot}
                          </div>
                        )}
                        {a.reasonForVisit && (
                          <div style={{ fontSize: 12, color: 'var(--color-ink-soft)', marginTop: 3, fontStyle: 'italic' }}>{a.reasonForVisit}</div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                        <span style={{ fontSize: 11, color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>#{a.tokenNumber}</span>
                        <StatusBadge status={a.status} />
                        <WhatsAppButton
                          patient={a}
                          slot={a.appointmentSlot}
                          date={a.appointmentDate}
                          tokenNumber={a.tokenNumber}
                          size="xs"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Month summary */}
          <div className="card lift3d-card" style={{ padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
              {MONTHS[viewMonth]} {viewYear} — Overview
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
              <SummaryRow label="Total appointments" value={appointments.length} />
              <SummaryRow label="Days with bookings" value={Object.keys(countByDate).length} />
              <SummaryRow label="Completed" value={appointments.filter((a) => a.status==='completed').length} />
              <SummaryRow label="Cancelled" value={appointments.filter((a) => a.status==='cancelled').length} />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  const [y,m,d] = iso.split('-');
  const date = new Date(Number(y), Number(m)-1, Number(d));
  return date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function SummaryRow({ label, value }) {
  return (
    <>
      <div style={{ color: 'var(--color-ink-soft)' }}>{label}</div>
      <div style={{ fontWeight: 700, textAlign: 'right' }}>{value}</div>
    </>
  );
}
